import * as babylon from "@babylonjs/core";
import lodash from "lodash";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";
import {
  mimicOrder,
  topologicalOrder,
} from "@/shared/state/visualization/schema/v1/three-d";

import {
  materialForColor,
  recolorMaterial,
} from "../../assetBuilders/materials";
import * as components from "../../components";
import { BabylonContext, BabylonState, Entity } from "../../state";
import {
  applyPointSizeOverride,
  disposePointSizeOverrides,
} from "../reloadStaleComponents/pointSizeOverride";
import { invalidateGlobalPositions } from "../reloadStaleComponents/reloadGlobalPositions";

/**
 * Sets the state's entities from the panel's registry and its components.
 *
 * The **registry** decides which entities exist — an entity declared there with
 * no components at all is legitimate, and is exactly what a glTF's structural
 * link nodes become. `next.entities` supplies whatever components each one has,
 * and is sparse relative to the registry.
 */
export function updateEntities(
  state: BabylonState,
  next: schema.PanelData,
  registry: schema.EntityRegistry,
) {
  const entitySetChanged =
    Object.keys(state.entities).length !== Object.keys(registry).length ||
    Object.keys(registry).some((id) => !state.entities[id]);

  // Remove entities that need to be removed.
  for (const entity of Object.values(state.entities)) {
    if (!registry[entity.id]) {
      // Dispose of all components on the entity.
      disposePosition(entity);
      disposeGlobalPosition(entity);
      disposeMesh(entity);
      disposePath(entity);
      disposeVectorField(entity);
      disposePointCloud(entity);
      delete state.entities[entity.id];
    }
  }

  // Update or create entities that need to be updated or created.
  let hierarchyChanged = entitySetChanged;
  const repositioned: schema.EntityID[] = [];
  for (const entityId of Object.keys(registry)) {
    // A declared entity may have no components; treat that as all-absent rather
    // than skipping it, so structural nodes still get a GlobalPosition.
    const nextSpec: schema.Entity = next.entities[entityId] ?? { id: entityId };
    let entity = state.entities[entityId];

    // Identify what change needs to happen.
    if (!entity) {
      // Create the entity if it doesn't exist.
      entity = {
        id: entityId,
      };
      state.entities[entityId] = entity;
    }
    if (updatePosition(entity, nextSpec.position)) {
      repositioned.push(entityId);
    }
    if (updateRotation(entity, nextSpec.rotation)) {
      // Same cascade as a position: everything below is composed through this.
      repositioned.push(entityId);
    }
    if (updateGlobalPosition(entity, registry[entityId].parent)) {
      hierarchyChanged = true;
    }
    updateMesh(entity, nextSpec.mesh, state.context);
    updatePath(entity, nextSpec.path);
    updateVectorField(entity, nextSpec.vectorField);
    updatePointCloud(entity, nextSpec.pointCloud);
  }

  // Recompute the resolution order only when the shape of the hierarchy moved.
  // Every frame reads it, so a per-frame sort would be wasted work.
  if (hierarchyChanged) {
    state.sceneConfig.entityOrder = topologicalOrder(registry);
  }

  // Cascade after the order is current, so descendants are found through the new
  // hierarchy. Covers a `Position` being added or removed outright; a change to
  // its data is caught by `reloadStalePositions` when the reload starts.
  for (const entityId of repositioned) {
    invalidateGlobalPositions(state, entityId);
  }

  // Invalidate the third-person zoom cache. `updateEntities` is the
  // funnel for every panel-state-driven mutation of `state.entities`
  // — adds, removes, mesh rescales — so a bump here covers the cases
  // that change the scene-union bounding box. Path-mesh creation
  // happens later in `reloadStalePaths`, which bumps independently.
  state.sceneConfig.entityGeneration =
    (state.sceneConfig.entityGeneration ?? 0) + 1;
}

/** @returns whether the component changed, so the caller can cascade staleness. */
function updatePosition(
  entity: Entity,
  spec: schema.Position | undefined,
): boolean {
  if (lodash.isEqual(entity.position?.spec, spec)) {
    return false;
  }
  // Always reset the position if a change has occurred.
  disposePosition(entity);
  // If the entity's position was traced, mark the trace as dirty and abort
  // any ongoing mesh construction.
  if (entity.path) {
    entity.path.load.state = "stale";
  }
  if (entity.vectorField) {
    entity.vectorField.meshStale = true;
  }
  // Remove deleted positions
  if (!spec) {
    return true;
  }

  entity.position = {
    spec,
    current: { logtime: 0n, vector: babylon.Vector3.Zero() },
    mean: babylon.Vector3.Zero(),
    cache: {
      load: {
        abortController: new AbortController(),
        state: "stale",
      },
      logtimes: [0n],
      allPoints: [babylon.Vector3.Zero()],
    },
  };

  // Pre-set any constant vector components from the spec.
  if (typeof spec.right === "number") {
    const x = spec.right * spec.rightScale;
    entity.position.current.vector.x = x;
    entity.position.mean.x = x;
  }
  if (typeof spec.forward === "number") {
    const z = spec.forward * spec.forwardScale;
    entity.position.current.vector.z = z;
    entity.position.mean.z = z;
  }
  if (typeof spec.up === "number") {
    const y = spec.up * spec.upScale;
    entity.position.current.vector.y = y;
    entity.position.mean.y = y;
  }
  return true;
}

/**
 * Reconciles the entity's `GlobalPosition` with its configured parent.
 *
 * Created for *every* entity, whatever its spec — a structural hierarchy node
 * with no `Position` of its own still has to hand a global down to its children,
 * and the render systems gate on this component rather than on `Position`.
 *
 * @returns whether the hierarchy changed, so the caller knows to recompute the
 * resolution order. This is the single place that detects that.
 */
function updateGlobalPosition(
  entity: Entity,
  nextSpec: schema.EntityID | undefined,
): boolean {
  if (entity.globalPosition) {
    if (entity.globalPosition.spec === nextSpec) {
      return false;
    }
    entity.globalPosition.spec = nextSpec;
    // The accumulated trajectory was summed over the old ancestor chain, so it
    // no longer describes where this entity has been.
    entity.globalPosition.cache.load.state = "stale";
    return true;
  }

  entity.globalPosition = {
    spec: nextSpec,
    current: babylon.Vector3.Zero(),
    // Pre-allocated, because `resolveGlobalPositions` writes into it every frame
    // rather than replacing it.
    rotation: babylon.Quaternion.Identity(),
    mean: babylon.Vector3.Zero(),
    cache: {
      load: { state: "stale" },
      logtimes: [],
      allPoints: [],
    },
  };
  return true;
}

/**
 * Drops the component. Unlike every other dispose function here it releases no
 * Babylon resource — a `GlobalPosition` is pure arithmetic state.
 */
function disposeGlobalPosition(entity: Entity) {
  delete entity.globalPosition;
}

/**
 * @returns whether the component changed, so the caller can cascade staleness.
 *
 * A rotation propagates to descendants exactly as a position does — a child's
 * offset is placed by its parent's orientation — so adding or removing one has
 * to invalidate every global below it.
 */
function updateRotation(
  entity: Entity,
  spec: schema.Rotation | undefined,
): boolean {
  if (lodash.isEqual(entity.rotation?.spec, spec)) {
    return false;
  }
  disposeRotation(entity);
  if (entity.path) {
    entity.path.load.state = "stale";
  }
  if (entity.vectorField) {
    entity.vectorField.meshStale = true;
  }
  if (!spec) {
    return true;
  }

  // Starts stale, and at no rotation: `reloadStaleRotations` fills the cache in,
  // resolving a constant spec without a fetch on the very next poll.
  entity.rotation = {
    spec,
    current: { logtime: 0n, quaternion: babylon.Quaternion.Identity() },
    cache: {
      load: {
        abortController: new AbortController(),
        state: "stale",
      },
      logtimes: [0n],
      allRotations: [babylon.Quaternion.Identity()],
    },
  };
  return true;
}

function disposeRotation(entity: Entity) {
  entity.rotation?.cache.load.abortController?.abort();
  if (entity.path) {
    entity.path.load.state = "stale";
  }
  if (entity.vectorField) {
    entity.vectorField.meshStale = true;
  }
  delete entity.rotation;
}

function disposePosition(entity: Entity) {
  entity.position?.cache.load.abortController?.abort();
  if (entity.path) {
    entity.path.load.state = "stale";
  }
  if (entity.vectorField) {
    entity.vectorField.meshStale = true;
  }
  delete entity.position;
}

function updateMesh(
  entity: Entity,
  nextSpec: schema.Mesh | undefined,
  context: BabylonContext,
) {
  if (lodash.isEqual(entity.mesh?.spec, nextSpec)) {
    return;
  }
  const rescaleMesh = (mesh: babylon.AbstractMesh, scale: number) => {
    mesh.scaling = new babylon.Vector3(scale, scale, scale);
  };

  const existing = entity.mesh;

  // A `model` of the same file is reconciled in place, never rebuilt. Binding a
  // joint or editing another one's scale changes this spec, and tearing the
  // model down for that would drop and re-instantiate the whole copy — a visible
  // flash on every keystroke in the settings sidebar. Only the file identifies
  // the geometry, so only the file changing means a different model.
  if (
    existing?.model &&
    existing.spec.type === "model" &&
    nextSpec?.type === "model" &&
    lodash.isEqual(existing.spec.file, nextSpec.file)
  ) {
    const previousPointSize =
      existing.spec.type === "model" ? existing.spec.pointSize : undefined;
    existing.spec = nextSpec;
    existing.model.jointOrder = mimicOrder(nextSpec.joints ?? []);
    updateJointCaches(existing, nextSpec.jointValues);
    // Same-file edits never re-stale the copy, so the size has to follow the
    // spec through this path. Before the copy has loaded there is nothing to
    // dress; `reloadStaleMeshes` applies from the updated spec when it lands.
    if (
      existing.model.instance !== undefined &&
      nextSpec.pointSize !== previousPointSize
    ) {
      applyPointSizeOverride(existing.model, nextSpec.pointSize);
    }
    return;
  }

  if (existing?.mesh && nextSpec) {
    const currentSpec = existing.spec;
    // A primitive of unchanged type can be mutated in place rather than rebuilt.
    // A `model_node` cannot: any change to it means different geometry, which
    // has to be re-fetched.
    if (
      currentSpec.type === nextSpec.type &&
      isPrimitive(nextSpec) &&
      isPrimitive(currentSpec)
    ) {
      if (currentSpec.color !== nextSpec.color && existing.material) {
        recolorMaterial(existing.material, nextSpec.color);
      }
      if (nextSpec.type === "box") {
        rescaleMesh(existing.mesh, nextSpec.size);
      }
      if (nextSpec.type === "sphere") {
        rescaleMesh(existing.mesh, nextSpec.radius);
      }
      existing.spec = nextSpec;
      return;
    }
  }

  // Otherwise, always reset this component.
  disposeMesh(entity);
  if (!nextSpec) {
    return;
  }

  if (nextSpec.type === "model_node") {
    // Geometry has to be fetched and parsed, so the component starts empty and
    // `reloadStaleMeshes` fills it in. Nothing is drawn until then.
    entity.mesh = {
      spec: nextSpec,
      load: { state: "stale", abortController: new AbortController() },
    };
    return;
  }

  if (nextSpec.type === "model") {
    // The anchor exists before the file does, so `applyRenderOrigin` has
    // somewhere to write from the very first frame and the copy lands already
    // placed rather than at the origin for a frame.
    const anchor = new babylon.TransformNode(
      `${entity.id}.model`,
      context.scene,
    );
    // Set explicitly, because Babylon otherwise composes from the euler
    // `rotation` field and ignores a quaternion assigned later in the frame.
    anchor.rotationQuaternion = babylon.Quaternion.Identity();
    // Released with the anchor, which disposes its descendants.
    const axes = new babylon.TransformNode(
      `${entity.id}.model.axes`,
      context.scene,
    );
    axes.parent = anchor;
    axes.rotationQuaternion = babylon.Quaternion.Identity();
    const model: components.Model = {
      anchor,
      axes,
      nodesByName: new Map(),
      jointOrder: mimicOrder(nextSpec.joints ?? []),
      dispose: () => {
        // Clones first: they dress meshes the instance dispose is about to
        // release, and nothing else holds them.
        disposePointSizeOverrides(model);
        model.instance?.dispose();
        model.instance = undefined;
        model.nodesByName.clear();
        anchor.dispose();
      },
    };
    entity.mesh = {
      spec: nextSpec,
      model,
      load: { state: "stale", abortController: new AbortController() },
      jointCaches: {},
    };
    updateJointCaches(entity.mesh, nextSpec.jointValues);
    return;
  }

  const material = materialForColor(
    `${entity.id}.material`,
    nextSpec.color,
    context.scene,
  );
  let mesh: babylon.Mesh;
  switch (nextSpec.type) {
    case "sphere":
      mesh = babylon.MeshBuilder.CreateSphere(entity.id, { diameter: 2 });
      rescaleMesh(mesh, nextSpec.radius);
      break;
    case "box":
      mesh = babylon.MeshBuilder.CreateBox(entity.id, { size: 1 });
      rescaleMesh(mesh, nextSpec.size);
      break;
  }
  mesh.material = material;
  context.scene.addMesh(mesh);
  entity.mesh = {
    spec: nextSpec,
    material,
    mesh,
  };
}

/**
 * Whether this is one of the procedurally built shapes.
 *
 * The two model variants are the exception in every branch that reads `color` or
 * a size: both take their geometry and materials from a file, so neither has a
 * color or a size to read. Narrowing through one predicate keeps those branches
 * honest as further variants are added.
 */
function isPrimitive(spec: schema.Mesh): spec is schema.Sphere | schema.Box {
  return spec.type === "sphere" || spec.type === "box";
}

/**
 * Reconciles one model's joint-value caches against the specs it now declares.
 *
 * Keyed by joint name and diffed per joint rather than rebuilt wholesale,
 * because editing one joint's binding must not restart the fetches of the eleven
 * others: a robot's joints are typically bound one after another, and a
 * wholesale rebuild would re-request every timeline on each edit.
 *
 * A cache starts `stale` and empty; `reloadStaleJointValues` fills it in,
 * resolving a constant without a fetch on the very next poll.
 */
function updateJointCaches(
  mesh: components.Mesh,
  specs: Record<string, schema.JointValue> | undefined,
) {
  const caches = (mesh.jointCaches ??= {});
  for (const [name, cache] of Object.entries(caches)) {
    if (!specs?.[name]) {
      cache.load.abortController.abort();
      delete caches[name];
    }
  }
  for (const [name, spec] of Object.entries(specs ?? {})) {
    if (lodash.isEqual(caches[name]?.spec, spec)) {
      continue;
    }
    caches[name]?.load.abortController.abort();
    caches[name] = {
      spec,
      load: { state: "stale", abortController: new AbortController() },
      logtimes: [],
      values: [],
    };
  }
}

function disposeMesh(entity: Entity) {
  // Cancels an in-flight model load so its geometry is not adopted after the
  // component it belonged to is gone.
  entity.mesh?.load?.abortController.abort();
  for (const cache of Object.values(entity.mesh?.jointCaches ?? {})) {
    cache.load.abortController.abort();
  }
  entity.mesh?.material?.dispose();
  entity.mesh?.mesh?.dispose();
  entity.mesh?.model?.dispose();
  delete entity.mesh;
}

function updatePath(entity: Entity, spec: schema.Path | undefined) {
  if (lodash.isEqual(entity.path?.spec, spec)) {
    return;
  }
  // Always reset the path if a change has occurred.
  disposePath(entity);
  if (!spec) {
    return;
  }

  entity.path = {
    spec,
    mesh: undefined,
    load: {
      state: "stale",
    },
  };
  // The stale path mesh will be built once the entity's position data is loaded.
}

function disposePath(entity: Entity) {
  entity.path?.mesh?.dispose();
  entity.path?.material?.dispose();
  delete entity.path;
}

/**
 * Returns the subset of VectorField spec properties that affect the underlying
 * data (axis bindings and scale factors). Changes to these require a full cache
 * reload. All other properties (color, lineWidth, stride, displayMode, gradient
 * settings) are display-only and can be applied by rebuilding the mesh alone.
 */
function vectorFieldDataSpec(spec: schema.VectorField) {
  return {
    right: spec.right,
    rightScale: spec.rightScale,
    up: spec.up,
    upScale: spec.upScale,
    forward: spec.forward,
    forwardScale: spec.forwardScale,
  };
}

function updateVectorField(
  entity: Entity,
  spec: schema.VectorField | undefined,
) {
  if (lodash.isEqual(entity.vectorField?.spec, spec)) {
    return;
  }

  // If removing the vector field, dispose everything.
  if (!spec) {
    disposeVectorField(entity);
    return;
  }

  // If the vector field already exists and only display properties changed
  // (not the axis bindings / scales), keep the cached data and just rebuild
  // the mesh with the new visual settings.
  if (
    entity.vectorField &&
    lodash.isEqual(
      vectorFieldDataSpec(entity.vectorField.spec),
      vectorFieldDataSpec(spec),
    )
  ) {
    disposeVectorFieldMeshes(entity.vectorField);
    entity.vectorField.gradient = undefined;
    entity.vectorField.spec = spec;
    entity.vectorField.meshStale = true;
    return;
  }

  // Data-affecting properties changed — full teardown and reload.
  disposeVectorField(entity);

  entity.vectorField = {
    spec,
    mesh: undefined,
    currentMesh: undefined,
    current: babylon.Vector3.Zero(),
    cache: {
      logtimes: [],
      allVectors: [],
      load: {
        abortController: new AbortController(),
        state: "stale",
      },
    },
    meshStale: true,
  };
}

/** Disposes the mesh and currentMesh (and their materials) on a vector field. */
function disposeVectorFieldMeshes(vf: components.VectorField) {
  vf.mesh?.material?.dispose();
  vf.mesh?.dispose();
  vf.currentMesh?.material?.dispose();
  vf.currentMesh?.dispose();
  vf.mesh = undefined;
  vf.currentMesh = undefined;
}

function disposeVectorField(entity: Entity) {
  if (entity.vectorField) {
    entity.vectorField.cache.load.abortController?.abort();
    disposeVectorFieldMeshes(entity.vectorField);
  }
  delete entity.vectorField;
}

/**
 * The subset of a PointCloud spec that decides which vertices are fetched and
 * decoded: the topic, the axis mapping and the stride. A change here means the
 * index and every frame are stale. Everything else — color, color mode, point
 * size — only changes how the same vertices are drawn.
 */
function pointCloudDataSpec(spec: schema.PointCloud) {
  return {
    source: spec.source,
    right: spec.right,
    forward: spec.forward,
    up: spec.up,
    stride: spec.stride,
  };
}

function updatePointCloud(entity: Entity, spec: schema.PointCloud | undefined) {
  if (lodash.isEqual(entity.pointCloud?.spec, spec)) {
    return;
  }

  if (!spec) {
    disposePointCloud(entity);
    return;
  }

  // Display-only change: keep the index and the frame on screen, and let the
  // reload poll re-dress the mesh.
  if (
    entity.pointCloud &&
    lodash.isEqual(
      pointCloudDataSpec(entity.pointCloud.spec),
      pointCloudDataSpec(spec),
    )
  ) {
    entity.pointCloud.spec = spec;
    entity.pointCloud.displayStale = true;
    return;
  }

  // Data change: full teardown. The index reloads, the frames held are let
  // go, and the mesh is rebuilt on the first frame that arrives for the new
  // spec.
  disposePointCloud(entity);
  entity.pointCloud = components.createPointCloud(spec);
}

function disposePointCloud(entity: Entity) {
  // Cancels the index load and any in-flight frame so neither is adopted after
  // the component it belonged to is gone, and lets the frames held go.
  entity.pointCloud?.index.load.abortController.abort();
  entity.pointCloud?.fetch?.abortController.abort();
  entity.pointCloud?.cache.clear();
  entity.pointCloud?.mesh?.dispose();
  // With its textures: a gradient's color strip is the material's emissive.
  entity.pointCloud?.material?.dispose(false, true);
  delete entity.pointCloud;
}
